import React, { useState, useMemo } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {FileText, Calendar, Eye, Download, Mail, Search, X, CheckCircle} from 'lucide-react';
import ReviewSubmitPage from '../admin/ReviewSubmitPage';
import { apiRequest } from '../../config/api';
import { toast } from '../ui/use-toast';
import { pdf } from '@react-pdf/renderer';
import PatientRecordPDF from '../pdf/PatientRecordPDF';

const DoctorRecentActivity = ({submissions, appointments = [], profile}) => {
  const [viewingSubmission, setViewingSubmission] = useState(null);
  const [submissionData, setSubmissionData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const handleView = async (submission) => {
    setViewingSubmission(submission);
    setLoadingData(true);
    
    try {
      const data = await apiRequest(`get_patient_record_submission.php?submission_id=${submission.id}`);
      if (data.success) {
        const transformedData = {
          ...data,
          procedures: Array.isArray(data.procedures) 
            ? { rows: data.procedures }
            : (data.procedures || null),
          doctor_name: data.doctor_name || submission.doctors?.name || null
        };
        setSubmissionData(transformedData);
      } else {
        toast({
          title: 'Error',
          description: 'Failed to load submission details',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to load submission details',
        variant: 'destructive'
      });
    } finally {
      setLoadingData(false);
    }
  };

  const handlePrint = async (submission) => {
    try {
      let pdfUrl = submission.pdf_url;
      
      if (!pdfUrl) {
        const data = await apiRequest(`get_patient_record_submission.php?submission_id=${submission.id}`);
        if (data.success) {
          pdfUrl = data.pdf_url;
        }
      }

      if (pdfUrl) {
        window.open(pdfUrl, '_blank');
        console.log('Opening PDF URL:', pdfUrl);
      } else {
        toast({
          title: 'Error',
          description: 'PDF not available for this submission',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error printing:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to open PDF',
        variant: 'destructive'
      });
    }
  };

  const handleEmail = async (submission) => {
    try {
      let pdfUrl = submission.pdf_url;
      
      if (!pdfUrl) {
        const data = await apiRequest(`get_patient_record_submission.php?submission_id=${submission.id}`);
        if (data.success) {
          pdfUrl = data.pdf_url;
        }
      }

      // Send email - backend will use PDF URL from submission record in database
      const emailData = await apiRequest('email_patient_record.php', {
        method: 'POST',
        body: JSON.stringify({ 
          submission_id: submission.id
          // Note: Backend uses PDF URL from submission record (ensures correct PDF per appointment)
        })
      });
      
      if (emailData.success) {
        toast({
          title: 'Success',
          description: 'Patient record sent via email with PDF download link'
        });
      } else {
        // Check if it's a configuration error (Brevo disabled)
        const isConfigError = emailData.message?.includes('disabled') || 
                              emailData.message?.includes('not configured') ||
                              emailData.message?.includes('API key');
        
        toast({
          title: 'Email Service Not Configured',
          description: isConfigError 
            ? 'Email service is not configured. Please contact your administrator to set up email service.'
            : (emailData.message || emailData.error || 'Failed to send email'),
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error sending email:', error);
      const errorMessage = error.message || 'Failed to send email';
      const isConfigError = errorMessage.includes('disabled') || 
                           errorMessage.includes('not configured') ||
                           errorMessage.includes('Brevo email service is disabled');
      
      toast({
        title: isConfigError ? 'Email Service Not Configured' : 'Error',
        description: isConfigError
          ? 'Email service is not configured. Please contact your administrator to set up email service.'
          : errorMessage,
        variant: 'destructive'
      });
    }
  };

  // Combine submissions and appointments for Recent Activity
  // Only show completed submissions and scheduled appointments
  const combinedRecords = useMemo(() => {
    const records = [];
    
    // Add only completed submissions
    submissions.forEach(sub => {
      const status = (sub.status || '').toLowerCase();
      // Only include completed submissions
      if (status === 'completed') {
        records.push({
          ...sub,
          type: 'submission',
          recordDate: sub.submitted_at || sub.submittedAt,
          patientName: sub.profiles?.name || sub.patientName,
          doctorName: sub.doctors?.name,
          status: sub.status || 'completed'
        });
      }
    });
    
    // Add appointments with type 'appointment' (only scheduled)
    appointments.forEach(apt => {
      const status = (apt.status || '').toLowerCase();
      // Only include scheduled appointments
      if (status === 'scheduled') {
        // Check if appointment was booked by patient
        const isPatientBooked = apt.creator_type === 'patient' || apt.creator_patient_name;
        // Format creator name with "Dr." prefix only if creator is a doctor (NOT a patient)
        const getBookedByText = () => {
          if (!apt.creator_name && !apt.creator_patient_name) return null;
          const creatorName = apt.creator_name || apt.creator_patient_name;
          
          // If it's a patient, don't add "Dr." prefix
          if (isPatientBooked) {
            return creatorName;
          }
          
          // For users, check if they are a doctor
          const isDoctor = apt.creator_type === 'doctor' || 
                          apt.creator_role === 'doctor' ||
                          apt.creator_type === 'chief_doctor' ||
                          apt.creator_role === 'chief_doctor' ||
                          (apt.creator_type === 'user' && (apt.creator_role === 'doctor' || apt.creator_role === 'chief_doctor'));
          
          if (isDoctor) {
            return `Dr. ${creatorName}`;
          }
          
          // For other roles (admin, staff, etc.), don't add "Dr."
          return creatorName;
        };
        const bookedByText = getBookedByText();
        
        records.push({
          ...apt,
          type: 'appointment',
          recordDate: apt.appointment_date || apt.appointmentDate,
          patientName: apt.patient_name || apt.patientName || apt.profiles?.name,
          doctorName: apt.doctor_name || apt.doctors?.name,
          status: apt.status || 'Scheduled',
          bookedBy: bookedByText,
          isPatientBooked: isPatientBooked
        });
      }
    });
    
    // Sort by date (most recent first)
    return records.sort((a, b) => {
      const dateA = new Date(a.recordDate || 0);
      const dateB = new Date(b.recordDate || 0);
      return dateB - dateA;
    });
  }, [submissions, appointments]);

  // Filter combined records
  const filteredRecords = useMemo(() => {
    return combinedRecords.filter(record => {
      // Combined search - search both patient and doctor names
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase();
        const patientName = (record.patientName || '').toLowerCase();
        const doctorName = (record.doctorName || '').toLowerCase();
        
        // Match if search term is found in either patient name or doctor name
        if (!patientName.includes(searchLower) && !doctorName.includes(searchLower)) {
          return false;
        }
      }
      
      // Status filter
      if (statusFilter !== 'all') {
        const recordStatus = (record.status || '').toLowerCase();
        const filterStatus = statusFilter.toLowerCase();
        
        // Map status values
        if (filterStatus === 'submitted') {
          if (record.type === 'submission' && recordStatus !== 'completed') return false;
          if (record.type === 'appointment' && recordStatus !== 'submitted') return false;
        } else if (filterStatus === 'completed') {
          if (recordStatus !== 'completed') return false;
        } else if (filterStatus === 'pending') {
          if (recordStatus !== 'pending' && recordStatus !== 'in-progress') return false;
        } else if (filterStatus === 'scheduled') {
          if (record.type !== 'appointment' || recordStatus !== 'scheduled') return false;
        } else {
          if (recordStatus !== filterStatus) return false;
        }
      }
      
      return true;
    });
  }, [combinedRecords, searchTerm, statusFilter]);

  return (
    <>
      <Card className="glass-effect border-gray-700 mt-8">
        <CardHeader>
          <CardTitle className="text-white">Recent Activity</CardTitle>
          <CardDescription className="text-gray-300">
            Latest submissions and appointments
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="mb-4 space-y-3">
            <div className="flex flex-wrap gap-3 items-end">
              {/* Combined Search Box */}
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs text-gray-400 mb-1 block">Search Patient or Doctor</label>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search by patient or doctor name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="bg-gray-800 border-gray-600 text-white pl-8 h-9"
                  />
                </div>
              </div>
              
              {/* Status Filter */}
              <div className="flex-1 min-w-[150px]">
                <label className="text-xs text-gray-400 mb-1 block">Status</label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white h-9">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="submitted">Submitted</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="in-progress">In Progress</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Clear Filters Button */}
              {(searchTerm || statusFilter !== 'all') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                  }}
                  className="bg-gray-800 border-gray-600 text-white h-9"
                >
                  <X className="h-3 w-3 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </div>
          
          {filteredRecords.length > 0
            ? <div className="space-y-3">
                {filteredRecords.slice(0, 20).map(record => {
                  // Handle both submission and appointment records
                  const isSubmission = record.type === 'submission';
                  const submission = isSubmission ? record : null;
                  const appointment = !isSubmission ? record : null;
                  
                  return (
                  <div
                    key={`${record.type}-${record.id}`}
                    className="p-3 bg-gray-800 rounded-lg flex items-center justify-between gap-2 sm:gap-3 flex-wrap"
                  >
                    {/* Left side: Badge, Patient name, Doctor name */}
                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                      {/* Status Badge - Icon only on mobile portrait */}
                      {record.status === 'completed' || record.status === 'Completed' ? (
                        <span className="md:hidden w-6 h-6 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="h-3 w-3 text-green-100" />
                        </span>
                      ) : record.status === 'Scheduled' || record.status === 'scheduled' ? (
                        <span className="md:hidden w-6 h-6 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0">
                          <Calendar className="h-3 w-3 text-purple-100" />
                        </span>
                      ) : null}
                      
                      {/* Status Badge - Text on desktop */}
                      <span
                        className={`hidden md:flex px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${
                          record.status === 'completed' || record.status === 'Completed'
                            ? 'bg-green-600 text-green-100' 
                            : record.status === 'Scheduled' || record.status === 'scheduled'
                            ? 'bg-purple-600 text-purple-100'
                            : 'bg-gray-600 text-gray-100'
                        }`}
                      >
                        {record.status === 'completed' || record.status === 'Completed' 
                          ? 'Completed' 
                          : record.status === 'Scheduled' || record.status === 'scheduled'
                          ? 'Scheduled'
                          : record.status}
                      </span>
                      
                      {/* Type Icon */}
                      {isSubmission ? (
                        <FileText className="h-4 w-4 text-blue-400 flex-shrink-0" />
                      ) : (
                        <Calendar className="h-4 w-4 text-orange-400 flex-shrink-0" />
                      )}
                      
                      {/* Patient Name - Always visible */}
                      <p className="text-white font-medium truncate text-sm sm:text-base">
                        {record.patientName || 'Unknown Patient'}
                      </p>
                      
                      {/* Doctor Name - Always visible */}
                      {record.doctorName && (
                        <>
                          <span className="text-gray-500 hidden sm:inline">•</span>
                          <p className="text-gray-400 text-xs sm:text-sm truncate">
                            Dr. {record.doctorName}
                          </p>
                        </>
                      )}
                      
                      {/* Booked By - Only for appointments */}
                      {!isSubmission && record.bookedBy && (
                        <>
                          <span className="text-gray-500 hidden sm:inline">•</span>
                          <p className={`text-xs sm:text-sm truncate ${
                            record.isPatientBooked 
                              ? 'text-blue-300' 
                              : 'text-gray-400'
                          }`}>
                            Booked by: {record.bookedBy}
                          </p>
                        </>
                      )}
                    </div>
                    
                    {/* Right side: Action Buttons (only for submissions) */}
                    {isSubmission && (
                      <div className="flex gap-1 sm:gap-2 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleView(submission)}
                          className="bg-blue-600/20 border-blue-500 text-blue-300 hover:bg-blue-600/30 text-xs px-1.5 sm:px-2 py-1 h-7 w-7 sm:w-auto"
                          title="View"
                        >
                          <Eye className="h-3 w-3 sm:mr-1" />
                          <span className="hidden sm:inline">View</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handlePrint(submission)}
                          className="bg-green-600/20 border-green-500 text-green-300 hover:bg-green-600/30 text-xs px-1.5 sm:px-2 py-1 h-7 w-7 sm:w-auto"
                          title="Print"
                        >
                          <Download className="h-3 w-3 sm:mr-1" />
                          <span className="hidden sm:inline">Print</span>
                        </Button>
                        {(submission.profiles?.email || submission.patient_email) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEmail(submission)}
                            className="bg-purple-600/20 border-purple-500 text-purple-300 hover:bg-purple-600/30 text-xs px-1.5 sm:px-2 py-1 h-7 w-7 sm:w-auto"
                            title="Email"
                          >
                            <Mail className="h-3 w-3 sm:mr-1" />
                            <span className="hidden sm:inline">Email</span>
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            : <div className="text-center py-8">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-400">No records found</p>
                <p className="text-gray-500 text-sm mt-2">Try adjusting your filters</p>
              </div>}
        </CardContent>
      </Card>

      {/* View Submission Dialog */}
      <Dialog open={viewingSubmission !== null} onOpenChange={() => setViewingSubmission(null)}>
        <DialogContent className="glass-effect border-gray-700 bg-gray-900/95 backdrop-blur-sm max-w-7xl w-[98vw] max-h-[95vh] p-0 flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-white text-xl">
                Patient Record and Invoice
              </DialogTitle>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={async () => {
                  if (!submissionData) return;
                  
                  try {
                    toast({
                      title: 'Generating PDF...',
                      description: 'Please wait while we create your PDF file.',
                    });

                    const patientName = submissionData?.patient_name || viewingSubmission?.profiles?.name || 'Patient';
                    const appointmentReferenceId = submissionData?.appointment_reference_id || submissionData?.appointment_id || viewingSubmission?.appointment_reference_id || viewingSubmission?.appointment_id || 'Record';
                    const date = new Date().toISOString().split('T')[0];
                    const sanitizedPatientName = patientName.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-');
                    // Add high-precision timestamp (milliseconds + random) to ensure absolute uniqueness and prevent overwrites
                    const timestamp = Date.now();
                    const randomSuffix = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
                    const uniqueId = `${timestamp}-${randomSuffix}`;
                    const filename = `Patient-Record-${sanitizedPatientName}-${appointmentReferenceId}-${date}-${uniqueId}.pdf`;

                    let proceduresForPDF = null;
                    if (submissionData.procedures) {
                      if (Array.isArray(submissionData.procedures)) {
                        proceduresForPDF = { rows: submissionData.procedures };
                      } else if (submissionData.procedures.rows || submissionData.procedures.procedures) {
                        proceduresForPDF = submissionData.procedures;
                      }
                    }

                    const doctorName = submissionData.doctor_name || viewingSubmission?.doctors?.name || 'N/A';
                    
                    const doc = (
                      <PatientRecordPDF
                        patientInfo={{
                          name: submissionData.patient_name || viewingSubmission?.profiles?.name || 'N/A',
                          phone: submissionData.patient_phone || viewingSubmission?.profiles?.phone || 'N/A',
                          address: submissionData.patient_address || viewingSubmission?.profiles?.address || 'N/A',
                        }}
                        appointmentDetails={{
                          referenceId: submissionData.appointment_reference_id || submissionData.appointment_id || 'N/A',
                          id: submissionData.appointment_id || viewingSubmission?.appointment_id || 'N/A',
                        }}
                        medicalHistory={submissionData.medical_history || null}
                        dentalExamination={submissionData.dental_examination || null}
                        procedures={proceduresForPDF}
                        profile={{
                          name: doctorName,
                          full_name: doctorName
                        }}
                      />
                    );

                    const blob = await pdf(doc).toBlob();
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = filename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);

                    toast({
                      title: 'PDF Saved',
                      description: `Patient record saved as ${filename}`,
                    });
                  } catch (error) {
                    console.error('Error generating PDF:', error);
                    toast({
                      title: 'Error',
                      description: error.message || 'Failed to generate PDF. Please try again.',
                      variant: 'destructive',
                    });
                  }
                }}
                className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white border-0 hover:from-blue-700 hover:to-cyan-700 shadow-lg hover:shadow-xl transition-all"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            </div>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto px-6 py-4" id="pdf-content-wrapper">
            {loadingData ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
              </div>
            ) : submissionData ? (
              <ReviewSubmitPage
                patientId={submissionData.patient_id}
                patientName={submissionData.patient_name}
                appointmentId={submissionData.appointment_id || viewingSubmission?.appointment_id || null}
                medicalHistory={submissionData.medical_history}
                dentalExamination={submissionData.dental_examination}
                procedures={
                  Array.isArray(submissionData.procedures) 
                    ? { rows: submissionData.procedures }
                    : submissionData.procedures || null
                }
                hideDialog={true}
                onBack={null}
                onSubmit={null}
                doctorName={submissionData.doctor_name || viewingSubmission?.doctors?.name || null}
              />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DoctorRecentActivity;

